import { getCookie } from './cookie-utils';
import { logNavigation, logPerformance } from './session-logger';

// Performance optimization: Cache URL construction results
const urlCache = new Map();
const URL_CACHE_MAX_SIZE = 100;
const URL_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Performance optimization: Cache domain detection
let cachedServiceInfo = null;
let cachedServiceInfoTime = 0;
const SERVICE_INFO_CACHE_TTL = 60 * 1000; // 1 minute

/**
 * Build service URL with proper context and database ID
 * @param {string} subdomain - Target subdomain (e.g., 's1', 's2', 'my')
 * @param {string|number} databaseId - Database ID for context
 * @param {string} path - Path to navigate to (default: '/dashboard')
 * @returns {string} - Complete URL for navigation
 */
export const buildServiceUrl = (subdomain, databaseId, path = '/dashboard') => {
  try {
    const currentHost = window.location.hostname;
    let baseDomain;
    
    // Determine base domain from current host
    if (currentHost.includes('.exacc.au')) {
      baseDomain = 'exacc.au';
    } else if (currentHost.includes('.ssx.au')) {
      baseDomain = 'ssx.au';
    } else if (currentHost.includes('.exx.au')) {
      baseDomain = 'exx.au';
    } else {
      // Fallback for localhost/development
      baseDomain = currentHost;
    }
    
    // Construct the URL
    const protocol = window.location.protocol;
    let targetUrl;
    
    if (baseDomain === currentHost) {
      // Development/localhost case
      targetUrl = `${protocol}//${currentHost}${path}`;
    } else {
      // Production case with subdomains
      targetUrl = `${protocol}//${subdomain}.${baseDomain}${path}`;
    }
    
    // Add database context if provided and path doesn't already include it
    if (databaseId && !path.includes('/:dbId/')) {
      // Check if we need to add database context to URL
      const dbParam = path.includes('?') ? `&dbId=${databaseId}` : `?dbId=${databaseId}`;
      targetUrl += dbParam;
    }
    
    return targetUrl;
  } catch (error) {
    console.error('🧭 URL Build Error:', error, { subdomain, databaseId, path });
    // Fallback to current domain with path
    return `${window.location.protocol}//${window.location.hostname}${path}`;
  }
};

/**
 * Navigate to client service page with optimized direct navigation
 * @param {string} serviceUrl - Full service URL or subdomain
 * @param {string|number} databaseId - Database ID for context
 * @param {string} path - Path to navigate to (default: '/dashboard')
 * @param {boolean} newTab - Whether to open in new tab (default: false)
 * @returns {boolean} - True if navigation was initiated successfully
 */
export const navigateToClientPage = (serviceUrl, databaseId, path = '/dashboard', newTab = false) => {
  const startTime = Date.now();
  
  try {
    let targetUrl;
    
    // Check if serviceUrl is a full URL or just a subdomain
    if (serviceUrl.startsWith('http://') || serviceUrl.startsWith('https://')) {
      // Full URL provided
      targetUrl = serviceUrl;
      
      // Add path if it's not already included
      if (!serviceUrl.includes(path) && path !== '/dashboard') {
        const url = new URL(serviceUrl);
        url.pathname = path;
        targetUrl = url.toString();
      }
      
      // Add database context if needed
      if (databaseId) {
        const url = new URL(targetUrl);
        if (!url.searchParams.has('dbId')) {
          url.searchParams.set('dbId', databaseId.toString());
        }
        targetUrl = url.toString();
      }
    } else {
      // Subdomain provided, build full URL
      targetUrl = buildServiceUrl(serviceUrl, databaseId, path);
    }
    
    if (!isTrustedUrl(targetUrl)) {
      logNavigation.blocked(targetUrl, 'Untrusted URL');
      return false;
    }

    logNavigation.attempt(targetUrl, { serviceUrl, databaseId, path, newTab });
    
    // Perform navigation
    if (newTab) {
      const newWindow = window.open(targetUrl, '_blank');
      if (!newWindow) {
        logNavigation.blocked(targetUrl, 'Popup blocked');
        return false;
      }
    } else {
      window.location.href = targetUrl;
    }
    
    logNavigation.success(targetUrl);
    logPerformance.navigationTime(targetUrl, Date.now() - startTime);
    return true;
  } catch (error) {
    logNavigation.failed(serviceUrl, error, { databaseId, path, newTab });
    return false;
  }
};

/**
 * Get current service information
 * @returns {Object} - Current service information
 */
export const getCurrentServiceInfo = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  
  return {
    hostname,
    subdomain: parts[0],
    domain: parts.slice(1).join('.'),
    isProduction: hostname.includes('.exacc.au') || hostname.includes('.ssx.au') || hostname.includes('.exx.au'),
    isDevelopment: hostname === 'localhost' || hostname.includes('127.0.0.1'),
    currentPath: window.location.pathname,
    currentSearch: window.location.search
  };
};

/**
 * Check if target URL is same origin for security
 * @param {string} targetUrl - URL to check
 * @returns {boolean} - True if same origin or trusted subdomain
 */
export const isTrustedNavigationUrl = (targetUrl, currentOrigin) => {
  try {
    const currentUrl = new URL(currentOrigin);
    const url = new URL(targetUrl, currentUrl);
    const currentHost = currentUrl.hostname;
    const isLocal = currentHost === 'localhost' || currentHost.startsWith('127.');
    if (isLocal) {
      return url.origin === currentUrl.origin;
    }
    if (
      url.protocol !== 'https:' ||
      url.username ||
      url.password ||
      url.port
    ) {
      return false;
    }

    const servicePrefix = /^(my|app|s1v)\./;
    const knownBaseDomains = ['exacc.au', 'ssx.au', 'exx.au', 'ipasoft.au'];
    let baseDomain = knownBaseDomains.find(
      (candidate) =>
        currentHost === candidate || currentHost.endsWith(`.${candidate}`),
    );

    if (currentHost.endsWith('.jimssoft.com')) {
      baseDomain = currentHost.replace(servicePrefix, '');
    } else if (currentHost.endsWith('.olivs.app')) {
      baseDomain = currentHost.replace(servicePrefix, '');
    }

    if (!baseDomain) {
      return false;
    }

    return new Set([
      `my.${baseDomain}`,
      `app.${baseDomain}`,
      // `s1v.${baseDomain}`,  // dead host - 404s on every path, no longer an allowed navigation target
    ]).has(url.hostname);
  } catch (error) {
    console.error('🧭 URL Trust Check Error:', error);
    return false;
  }
};

export const isTrustedUrl = (targetUrl) =>
  isTrustedNavigationUrl(targetUrl, window.location.origin);

export const getSafeRedirectUrl = (targetUrl, currentOrigin) => {
  const origin = new URL(currentOrigin);
  const loginRoot = new URL('/', origin).toString();
  if (!targetUrl || !isTrustedNavigationUrl(targetUrl, origin)) {
    return loginRoot;
  }
  return new URL(targetUrl, origin).toString();
};

/**
 * Navigate with session context (enhanced version of existing function)
 * @param {string} targetUrl - Target URL
 * @param {Object} options - Navigation options
 * @returns {boolean} - True if navigation was initiated
 */
export const navigateWithSession = (targetUrl, options = {}) => {
  const {
    newTab = false,
    databaseId = getCookie('databaseId'),
    validateUrl = true,
    fallbackUrl = null
  } = options;
  
  try {
    // Validate URL if requested
    if (validateUrl && !isTrustedUrl(targetUrl)) {
      console.error('🧭 Navigation Blocked: Untrusted URL', targetUrl);
      return false;
    }
    
    // Add database context if needed
    let finalUrl = targetUrl;
    if (databaseId) {
      const url = new URL(targetUrl);
      if (!url.searchParams.has('dbId')) {
        url.searchParams.set('dbId', databaseId.toString());
      }
      finalUrl = url.toString();
    }
    
    // Perform navigation
    if (newTab) {
      const newWindow = window.open(finalUrl, '_blank');
      if (!newWindow) {
        console.error('🧭 Navigation Error: Popup blocked');
        if (fallbackUrl) {
          window.location.href = fallbackUrl;
        }
        return false;
      }
    } else {
      window.location.href = finalUrl;
    }
    
    return true;
  } catch (error) {
    console.error('🧭 Session Navigation Error:', error);
    if (fallbackUrl) {
      window.location.href = fallbackUrl;
      return true;
    }
    return false;
  }
};

/**
 * Preload target page resources for faster navigation
 * @param {string} targetUrl - URL to preload
 */
export const preloadPage = (targetUrl) => {
  try {
    if (!isTrustedUrl(targetUrl)) {
      return;
    }
    
    // Create link element for DNS prefetch
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = new URL(targetUrl).origin;
    document.head.appendChild(link);
    
    // Create link element for preconnect
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = new URL(targetUrl).origin;
    document.head.appendChild(preconnect);
  } catch (error) {
    console.error('🧭 Preload Error:', error);
  }
};
